<template>
  <div class="step-salary-container">
    <!-- Заголовок -->
    <div class="step-header">
      <div class="step-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <rect
            x="3"
            y="6"
            width="18"
            height="14"
            rx="3"
            stroke="currentColor"
            stroke-width="1.8"
          />

          <path
            d="M8 6V4.8A1.8 1.8 0 0 1 9.8 3h4.4A1.8 1.8 0 0 1 16 4.8V6"
            stroke="currentColor"
            stroke-width="1.8"
          />

          <path
            d="M3.5 11.5c5.5 2 11.5 2 17 0M10 12.8h4"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
          />
        </svg>
      </div>

      <div class="step-heading">
        <span class="step-label">
          Условия работы
        </span>

        <h3 class="step-heading-title">
          Опыт и зарплата
        </h3>
      </div>

      <span class="optional-badge">
        Зарплата необязательна
      </span>
    </div>

    <p class="step-description">
      Укажите необходимый опыт кандидата и примерную зарплату.
      Открытые условия помогают получать больше релевантных откликов.
    </p>

    <!-- Опыт -->
    <section class="experience-card">
      <div class="section-heading">
        <div class="section-title-wrapper">
          <div class="section-small-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M7 7V5.5A2.5 2.5 0 0 1 9.5 3h5A2.5 2.5 0 0 1 17 5.5V7"
                stroke="currentColor"
                stroke-width="1.7"
              />

              <rect
                x="3"
                y="7"
                width="18"
                height="13"
                rx="3"
                stroke="currentColor"
                stroke-width="1.7"
              />

              <path
                d="M3.5 12.5c5.5 2 11.5 2 17 0"
                stroke="currentColor"
                stroke-width="1.7"
              />
            </svg>
          </div>

          <div>
            <span class="section-title">
              Необходимый опыт
            </span>

            <span class="section-subtitle">
              Сколько лет опыта потребуется кандидату
            </span>
          </div>
        </div>

        <Transition name="experience-value" mode="out-in">
          <div
            :key="experience"
            class="experience-value"
          >
            {{ formattedExperience }}
          </div>
        </Transition>
      </div>

      <div class="slider-control">
        <button
          type="button"
          class="experience-control-button"
          :disabled="experience <= MIN_EXPERIENCE"
          aria-label="Уменьшить необходимый опыт"
          @click="decreaseExperience"
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M5 10h10"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>
        </button>

        <div class="slider-wrapper">
          <input
            v-model.number="experience"
            type="range"
            :min="MIN_EXPERIENCE"
            :max="MAX_EXPERIENCE"
            step="1"
            class="experience-slider"
            :style="{
              '--experience-progress': experienceProgress
            }"
            aria-label="Необходимый опыт"
          />

          <div class="slider-labels">
            <span>Без опыта</span>
            <span>5 лет</span>
            <span>10+ лет</span>
          </div>
        </div>

        <button
          type="button"
          class="experience-control-button"
          :disabled="experience >= MAX_EXPERIENCE"
          aria-label="Увеличить необходимый опыт"
          @click="increaseExperience"
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M10 5v10M5 10h10"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </div>
    </section>

    <!-- Зарплата -->
    <section class="salary-section">
      <div class="field-heading">
        <div>
          <label
            class="field-label"
            for="vacancy-salary"
          >
            Зарплата
          </label>

          <span class="field-subtitle">
            Укажите примерную сумму
          </span>
        </div>

        <span class="optional-label">
          Необязательно
        </span>
      </div>

      <div
        class="salary-input-shell"
        :class="{
          focused: isSalaryFocused,
          filled: hasSalary
        }"
      >
        <div class="salary-icon">
          {{ currency }}
        </div>

        <input
          id="vacancy-salary"
          v-model="salary"
          type="text"
          class="salary-input"
          placeholder="Например: 150 000"
          inputmode="numeric"
          autocomplete="off"
          maxlength="30"
          @input="handleSalaryInput"
          @focus="isSalaryFocused = true"
          @blur="handleSalaryBlur"
        />

        <Transition name="status">
          <div
            v-if="hasSalary"
            class="salary-status"
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="m5.5 10.2 2.8 2.8 6.2-6.2"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </Transition>
      </div>

      <!-- Валюта -->
      <div class="currency-section">
        <span class="currency-label">
          Валюта
        </span>

        <div class="currency-selector">
          <button
            v-for="item in currencies"
            :key="item.symbol"
            type="button"
            class="currency-button"
            :class="{
              active: currency === item.symbol
            }"
            @click="selectCurrency(item.symbol)"
          >
            <span class="currency-symbol">
              {{ item.symbol }}
            </span>

            <span class="currency-name">
              {{ item.name }}
            </span>
          </button>
        </div>
      </div>

      <div class="salary-hint">
        <div class="hint-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="12"
              cy="12"
              r="9"
              stroke="currentColor"
              stroke-width="1.7"
            />

            <path
              d="M12 11v5M12 7.5v.2"
              stroke="currentColor"
              stroke-width="1.9"
              stroke-linecap="round"
            />
          </svg>
        </div>

        <p>
          Если оставить поле пустым, зарплата будет указана
          как «Обсуждается на собеседовании».
        </p>
      </div>
    </section>
  </div>
</template>

<script setup>
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  watch
} from "vue";

const emit = defineEmits([
  "complete-step",
  "valid",
  "notValid"
]);

const MIN_EXPERIENCE = 0;
const MAX_EXPERIENCE = 10;

const currencies = [
  {
    symbol: "₽",
    name: "RUB"
  },
  {
    symbol: "$",
    name: "USD"
  },
  {
    symbol: "€",
    name: "EUR"
  }
];

const salary = ref(
  localStorage.getItem("vacancy_salary") || ""
);

const currency = ref(
  localStorage.getItem("vacancy_currency") || "₽"
);

const storedExperience =
  localStorage.getItem("vacancy_experience");

const parsedExperience =
  storedExperience !== null
    ? Number.parseInt(storedExperience, 10)
    : 3;

const experience = ref(
  Number.isFinite(parsedExperience)
    ? Math.min(
        Math.max(
          parsedExperience,
          MIN_EXPERIENCE
        ),
        MAX_EXPERIENCE
      )
    : 3
);

const isSalaryFocused = ref(false);

const hasSalary = computed(() =>
  salary.value.trim().length > 0
);

const experienceProgress = computed(() => {
  const progress =
    (
      (experience.value - MIN_EXPERIENCE) /
      (MAX_EXPERIENCE - MIN_EXPERIENCE)
    ) * 100;

  return `${progress}%`;
});

const formattedExperience = computed(() => {
  const value = experience.value;

  if (value === 0) {
    return "Без опыта";
  }

  if (value >= MAX_EXPERIENCE) {
    return `${MAX_EXPERIENCE}+ лет`;
  }

  const lastDigit = value % 10;
  const lastTwoDigits = value % 100;

  if (
    lastDigit === 1 &&
    lastTwoDigits !== 11
  ) {
    return `${value} год`;
  }

  if (
    [2, 3, 4].includes(lastDigit) &&
    ![12, 13, 14].includes(lastTwoDigits)
  ) {
    return `${value} года`;
  }

  return `${value} лет`;
});

/*
  Шаг необязательный и всегда валиден.
*/
emit("valid");

function decreaseExperience() {
  if (experience.value > MIN_EXPERIENCE) {
    experience.value -= 1;
  }
}

function increaseExperience() {
  if (experience.value < MAX_EXPERIENCE) {
    experience.value += 1;
  }
}

function selectCurrency(symbol) {
  currency.value = symbol;
}

function handleSalaryInput(event) {
  /*
    Оставляем только цифры и пробелы,
    затем красиво группируем разряды.
  */
  const digits =
    event.target.value.replace(/\D/g, "");

  salary.value = digits
    ? Number(digits).toLocaleString("ru-RU")
    : "";
}

function handleSalaryBlur() {
  isSalaryFocused.value = false;
  salary.value = salary.value.trim();
}

function saveSalaryAndContinue() {
  localStorage.setItem(
    "vacancy_salary",
    salary.value.trim()
  );

  localStorage.setItem(
    "vacancy_currency",
    currency.value
  );

  localStorage.setItem(
    "vacancy_experience",
    String(experience.value)
  );

  emit("complete-step");
}

watch(
  salary,
  value => {
    localStorage.setItem(
      "vacancy_salary",
      value
    );
  }
);

watch(
  currency,
  value => {
    localStorage.setItem(
      "vacancy_currency",
      value
    );
  }
);

watch(
  experience,
  value => {
    localStorage.setItem(
      "vacancy_experience",
      String(value)
    );
  },
  {
    immediate: true
  }
);

onMounted(() => {
  document.addEventListener(
    "save-salary",
    saveSalaryAndContinue
  );

  emit("valid");
});

onUnmounted(() => {
  document.removeEventListener(
    "save-salary",
    saveSalaryAndContinue
  );
});
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.step-salary-container {
  width: 100%;
  max-width: 720px;

  display: flex;
  flex-direction: column;

  margin-left: 40px;
  padding: 26px;

  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(48, 132, 255, 0.075),
      transparent 250px
    ),
    #ffffff;

  border: 1px solid rgba(30, 50, 80, 0.07);
  border-radius: 24px;

  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.02),
    0 12px 34px rgba(15, 23, 42, 0.04);

  animation: card-appearance 0.4s ease both;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 13px;

  margin-bottom: 17px;
}

.step-icon {
  width: 43px;
  height: 43px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #ffffff;

  background:
    linear-gradient(
      145deg,
      #36a1ff 0%,
      #4078f3 55%,
      #625bea 100%
    );

  border-radius: 14px;

  box-shadow:
    0 8px 18px rgba(48, 126, 246, 0.23),
    inset 0 1px 0 rgba(255, 255, 255, 0.24);
}

.step-icon svg {
  width: 23px;
  height: 23px;
}

.step-heading {
  min-width: 0;

  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
}

.step-label {
  color: #8b94a4;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.step-heading-title {
  margin: 0;

  color: #20242c;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 18px;
  line-height: 1.25;
}

.optional-badge {
  flex-shrink: 0;

  padding: 7px 10px;

  color: #687385;
  background: rgba(113, 125, 145, 0.08);

  border: 1px solid rgba(113, 125, 145, 0.1);
  border-radius: 999px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
  font-weight: 600;
  line-height: 1;
}

.step-description {
  max-width: 650px;
  margin: 0 0 21px;

  color: #697282;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
  line-height: 1.65;
}

/* Experience */

.experience-card {
  width: 100%;
  max-width: 650px;

  margin-bottom: 24px;
  padding: 17px;

  background:
    linear-gradient(
      145deg,
      rgba(55, 137, 247, 0.055),
      rgba(79, 93, 226, 0.025)
    );

  border: 1px solid rgba(55, 137, 247, 0.11);
  border-radius: 17px;
}

.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;

  margin-bottom: 18px;
}

.section-title-wrapper {
  min-width: 0;

  display: flex;
  align-items: center;
  gap: 10px;
}

.section-small-icon {
  width: 36px;
  height: 36px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #4288ef;
  background: rgba(66, 136, 239, 0.09);

  border-radius: 11px;
}

.section-small-icon svg {
  width: 20px;
  height: 20px;
}

.section-title-wrapper > div {
  min-width: 0;

  display: flex;
  flex-direction: column;
  gap: 2px;
}

.section-title {
  color: #3f4753;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 11px;
}

.section-subtitle {
  color: #929aa7;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 8px;
}

.experience-value {
  flex-shrink: 0;

  padding: 7px 10px;

  color: #347bdb;
  background: #ffffff;

  border: 1px solid rgba(51, 126, 224, 0.1);
  border-radius: 10px;

  box-shadow:
    0 4px 12px rgba(30, 88, 170, 0.06);

  font-family: "Eastman-Bold", sans-serif;
  font-size: 11px;
}

.slider-control {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.slider-wrapper {
  min-width: 0;
  flex: 1;
}

.experience-control-button {
  width: 31px;
  height: 31px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  margin-top: -11px;
  padding: 0;

  cursor: pointer;

  color: #4388f5;
  background: #ffffff;

  border: 1px solid rgba(67, 136, 245, 0.13);
  border-radius: 10px;

  box-shadow:
    0 4px 11px rgba(37, 99, 187, 0.07);

  transition:
    color 0.2s ease,
    background-color 0.2s ease,
    transform 0.2s ease,
    opacity 0.2s ease;
}

.experience-control-button:hover:not(:disabled) {
  color: #ffffff;
  background: #4388f5;

  transform: translateY(-1px);
}

.experience-control-button:disabled {
  cursor: not-allowed;
  opacity: 0.38;
}

.experience-control-button svg {
  width: 16px;
  height: 16px;
}

.experience-slider {
  width: 100%;
  height: 5px;

  display: block;

  margin: 3px 0 0;

  cursor: pointer;

  appearance: none;
  -webkit-appearance: none;

  background:
    linear-gradient(
      to right,
      #4388f5 0%,
      #4388f5 var(--experience-progress),
      #dce4ef var(--experience-progress),
      #dce4ef 100%
    );

  border-radius: 999px;
  outline: none;
}

.experience-slider::-webkit-slider-thumb {
  width: 20px;
  height: 20px;

  appearance: none;
  -webkit-appearance: none;

  background: #ffffff;

  border: 5px solid #4388f5;
  border-radius: 50%;

  box-shadow:
    0 3px 9px rgba(42, 107, 207, 0.24);

  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease;
}

.experience-slider::-webkit-slider-thumb:hover {
  transform: scale(1.12);

  box-shadow:
    0 4px 12px rgba(42, 107, 207, 0.3);
}

.experience-slider::-moz-range-thumb {
  width: 12px;
  height: 12px;

  background: #ffffff;

  border: 5px solid #4388f5;
  border-radius: 50%;

  box-shadow:
    0 3px 9px rgba(42, 107, 207, 0.24);
}

.slider-labels {
  display: flex;
  align-items: center;
  justify-content: space-between;

  margin-top: 11px;

  color: #98a0ac;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 8px;
}

/* Salary */

.salary-section {
  width: 100%;
  max-width: 650px;
}

.field-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 15px;

  margin-bottom: 8px;
}

.field-heading > div {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.field-label {
  color: #424957;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 11px;
}

.field-subtitle {
  color: #979eaa;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 8px;
}

.optional-label {
  color: #969eaa;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 8px;
}

.salary-input-shell {
  width: 100%;
  min-height: 54px;

  display: flex;
  align-items: center;

  background: #f6f7f9;

  border: 1px solid #e1e4e9;
  border-radius: 14px;

  box-shadow:
    inset 0 1px 2px rgba(15, 23, 42, 0.025);

  transition:
    background-color 0.22s ease,
    border-color 0.22s ease,
    box-shadow 0.22s ease,
    transform 0.22s ease;
}

.salary-input-shell:hover {
  background: #fafbfc;
  border-color: #d4d9e1;
}

.salary-input-shell.focused {
  background: #ffffff;
  border-color: #4388f5;

  box-shadow:
    0 0 0 4px rgba(67, 136, 245, 0.11),
    0 8px 24px rgba(31, 96, 205, 0.08);

  transform: translateY(-1px);
}

.salary-input-shell.filled:not(.focused) {
  border-color: rgba(39, 162, 104, 0.35);
}

.salary-icon {
  width: 37px;
  height: 37px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  margin-left: 9px;

  color: #4388f5;
  background: rgba(67, 136, 245, 0.09);

  border-radius: 11px;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 15px;

  transition:
    color 0.2s ease,
    background-color 0.2s ease,
    transform 0.2s ease;
}

.salary-input-shell.focused .salary-icon {
  color: #ffffff;
  background: #4388f5;

  transform: scale(1.03);
}

.salary-input {
  min-width: 0;
  width: 100%;
  height: 52px;

  padding: 0 12px;

  color: #252b35;
  caret-color: #4388f5;

  background: transparent;
  border: none;
  outline: none;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 15px;
}

.salary-input::placeholder {
  color: #a5acb7;
  opacity: 1;
}

.salary-status {
  width: 27px;
  height: 27px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  margin-right: 13px;

  color: #28a46b;
  background: rgba(40, 164, 107, 0.1);

  border-radius: 50%;
}

.salary-status svg {
  width: 16px;
  height: 16px;
}

/* Currency */

.currency-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;

  margin-top: 13px;
}

.currency-label {
  color: #7e8795;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
}

.currency-selector {
  display: flex;
  align-items: center;

  padding: 4px;

  background: #f0f2f5;
  border-radius: 12px;
}

.currency-button {
  min-width: 69px;
  height: 34px;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;

  padding: 0 10px;

  cursor: pointer;

  color: #747d8b;
  background: transparent;

  border: none;
  border-radius: 9px;

  transition:
    color 0.2s ease,
    background-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

.currency-button:hover:not(.active) {
  color: #397edb;
  background: rgba(255, 255, 255, 0.68);
}

.currency-button.active {
  color: #2775dc;
  background: #ffffff;

  box-shadow:
    0 3px 9px rgba(30, 75, 140, 0.1);

  transform: translateY(-1px);
}

.currency-symbol {
  font-family: "Eastman-Bold", sans-serif;
  font-size: 13px;
}

.currency-name {
  font-family: "Eastman-Regular", sans-serif;
  font-size: 8px;
}

/* Hint */

.salary-hint {
  display: flex;
  align-items: flex-start;
  gap: 9px;

  margin-top: 18px;
  padding: 11px 12px;

  color: #667084;
  background: rgba(53, 132, 247, 0.055);

  border: 1px solid rgba(53, 132, 247, 0.1);
  border-radius: 12px;
}

.hint-icon {
  width: 17px;
  height: 17px;

  flex-shrink: 0;

  color: #4388f5;
}

.hint-icon svg {
  width: 17px;
  height: 17px;
}

.salary-hint p {
  margin: 0;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
  line-height: 1.55;
}

/* Animations */

@keyframes card-appearance {
  from {
    opacity: 0;
    transform: translateY(7px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.experience-value-enter-active,
.experience-value-leave-active,
.status-enter-active,
.status-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.experience-value-enter-from {
  opacity: 0;
  transform: translateY(-4px);
}

.experience-value-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

.status-enter-from,
.status-leave-to {
  opacity: 0;
  transform: scale(0.65);
}

/* Dark theme */

html[data-theme="dark"] .step-salary-container {
  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(54, 132, 255, 0.09),
      transparent 250px
    ),
    #1c1c1f;

  border-color: rgba(255, 255, 255, 0.07);

  box-shadow:
    0 14px 38px rgba(0, 0, 0, 0.14);
}

html[data-theme="dark"] .step-heading-title {
  color: #f1f2f4;
}

html[data-theme="dark"] .step-label {
  color: #777f8d;
}

html[data-theme="dark"] .optional-badge {
  color: #a3aab6;
  background: rgba(255, 255, 255, 0.055);
  border-color: rgba(255, 255, 255, 0.065);
}

html[data-theme="dark"] .step-description {
  color: #a5a9b2;
}

html[data-theme="dark"] .experience-card {
  background:
    linear-gradient(
      145deg,
      rgba(55, 137, 247, 0.085),
      rgba(79, 93, 226, 0.035)
    );

  border-color: rgba(70, 143, 244, 0.13);
}

html[data-theme="dark"] .section-title {
  color: #d9dce2;
}

html[data-theme="dark"] .section-subtitle {
  color: #777f8b;
}

html[data-theme="dark"] .section-small-icon {
  color: #70a3ef;
  background: rgba(67, 136, 245, 0.1);
}

html[data-theme="dark"] .experience-value {
  color: #83b0f2;
  background: #292a2e;
  border-color: #3a3c42;

  box-shadow:
    0 5px 13px rgba(0, 0, 0, 0.14);
}

html[data-theme="dark"] .experience-control-button {
  color: #69a0f2;
  background: #292a2f;
  border-color: #3c3e45;

  box-shadow:
    0 5px 13px rgba(0, 0, 0, 0.12);
}

html[data-theme="dark"]
  .experience-control-button:hover:not(:disabled) {
  color: #ffffff;
  background: #407fdc;
}

html[data-theme="dark"] .experience-slider {
  background:
    linear-gradient(
      to right,
      #5795ec 0%,
      #5795ec var(--experience-progress),
      #42454c var(--experience-progress),
      #42454c 100%
    );
}

html[data-theme="dark"]
  .experience-slider::-webkit-slider-thumb {
  background: #27282c;
  border-color: #609cf0;
}

html[data-theme="dark"]
  .experience-slider::-moz-range-thumb {
  background: #27282c;
  border-color: #609cf0;
}

html[data-theme="dark"] .slider-labels {
  color: #737b87;
}

html[data-theme="dark"] .field-label {
  color: #d8dae0;
}

html[data-theme="dark"] .field-subtitle,
html[data-theme="dark"] .optional-label {
  color: #747b86;
}

html[data-theme="dark"] .salary-input-shell {
  background: #28282c;
  border-color: #3b3b41;
}

html[data-theme="dark"] .salary-input-shell:hover {
  background: #2b2b30;
  border-color: #484850;
}

html[data-theme="dark"] .salary-input-shell.focused {
  background: #2d2d32;
  border-color: #548ff1;

  box-shadow:
    0 0 0 4px rgba(78, 143, 245, 0.13),
    0 9px 24px rgba(0, 0, 0, 0.14);
}

html[data-theme="dark"] .salary-input {
  color: #f1f2f4;
  caret-color: #669cf5;
}

html[data-theme="dark"] .salary-input::placeholder {
  color: #6f747e;
}

html[data-theme="dark"] .salary-icon {
  color: #74a8f2;
  background: rgba(67, 136, 245, 0.1);
}

html[data-theme="dark"]
  .salary-input-shell.focused
  .salary-icon {
  color: #ffffff;
  background: #4685df;
}

html[data-theme="dark"] .currency-label {
  color: #7a828e;
}

html[data-theme="dark"] .currency-selector {
  background: #27282c;
}

html[data-theme="dark"] .currency-button {
  color: #8c939e;
}

html[data-theme="dark"]
  .currency-button:hover:not(.active) {
  color: #9ec0f1;
  background: rgba(255, 255, 255, 0.045);
}

html[data-theme="dark"] .currency-button.active {
  color: #86b1f2;
  background: #38393f;

  box-shadow:
    0 4px 11px rgba(0, 0, 0, 0.17);
}

html[data-theme="dark"] .salary-hint {
  color: #9ba2ae;
  background: rgba(63, 137, 241, 0.07);
  border-color: rgba(74, 145, 246, 0.12);
}

/* Mobile */

@media (max-width: 430px) {
  .step-salary-container {
    width: 100%;
    max-width: none;

    margin-left: 0;
    padding: 20px 16px;

    border-radius: 18px;
  }

  .step-icon {
    width: 39px;
    height: 39px;

    border-radius: 12px;
  }

  .step-heading-title {
    font-size: 16px;
  }

  .optional-badge {
    display: none;
  }

  .step-description {
    font-size: 12px;
    line-height: 1.55;
  }

  .section-heading {
    align-items: flex-start;
  }

  .section-subtitle {
    max-width: 160px;
    line-height: 1.35;
  }

  .slider-control {
    gap: 9px;
  }

  .experience-control-button {
    width: 29px;
    height: 29px;

    margin-top: -10px;
  }

  .currency-section {
    align-items: stretch;
    flex-direction: column;
    gap: 7px;
  }

  .currency-selector {
    width: 100%;
  }

  .currency-button {
    flex: 1;
    min-width: 0;
  }

  .salary-input {
    font-size: 16px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .step-salary-container {
    animation: none;
  }

  .experience-control-button,
  .experience-slider::-webkit-slider-thumb,
  .salary-input-shell,
  .salary-icon,
  .currency-button,
  .experience-value-enter-active,
  .experience-value-leave-active,
  .status-enter-active,
  .status-leave-active {
    transition: none;
  }
}
</style>